import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/store/auth'
import { ApiError } from '@/api/conclave'
import { callbackRedirectUri, consumeState } from '@/lib/gateSso'

/** The registered gate callback (one_login_spec.md §4 step 3/4): the gate lands
 *  the browser here with ?code=&state=. We validate the parked state, hand the
 *  one-time code to the Conclave backend (which redeems it server-side and sets
 *  Conclave's OWN session cookie), then step into the app. The code never goes
 *  anywhere except the backend; no token ever rides the URL. */
export function GateCallback() {
  const api = useAuth((s) => s.api)
  const loadMe = useAuth((s) => s.loadMe)
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [error, setError] = useState<string | null>(null)
  // The code is single-use, so the exchange must fire exactly once — even under
  // StrictMode's dev double-mount.
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current) return
    fired.current = true

    const code = params.get('code')
    const { ok, from } = consumeState(sessionStorage, params.get('state'))
    if (!code || !ok) {
      setError('This sign-in link is stale or was already used — head back and try again.')
      return
    }
    api()
      .gateExchange(code, callbackRedirectUri(window.location.origin))
      .then(async () => {
        // The session cookie is set; re-probe so the store flips to authed,
        // then land where the visitor was headed (or home).
        await loadMe()
        navigate(from && from.startsWith('/') ? from : '/', { replace: true })
      })
      .catch((err) => {
        setError(
          err instanceof ApiError && err.message
            ? err.message
            : 'Your Diwata ID sign-in could not be completed — try again.',
        )
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (error) {
    return (
      <div className="cp-empty" role="alert">
        {error}
      </div>
    )
  }
  return (
    <div className="cp-empty" role="status">
      Finishing your Diwata ID sign-in…
    </div>
  )
}
